import React from 'react';
import { conditionView, COND_ICONS, COND_LABELS, STATE_COLORS, roundHalfUp } from '@/lib/conditions';

const T = {
  es: { franjas: { manana: 'Mañana', mediodia: 'Mediodía', tarde: 'Tarde' }, hours: { manana: '08–12', mediodia: '12–16', tarde: '16–20' }, rest: 'Resto del día' },
  en: { franjas: { manana: 'Morning', mediodia: 'Midday', tarde: 'Afternoon' }, hours: { manana: '08–12', mediodia: '12–16', tarde: '16–20' }, rest: 'Rest of the day' },
  de: { franjas: { manana: 'Morgen', mediodia: 'Mittag', tarde: 'Nachmittag' }, hours: { manana: '08–12', mediodia: '12–16', tarde: '16–20' }, rest: 'Rest des Tages' },
};
const FRANJA_END = { manana: 12, mediodia: 16, tarde: 20 };
const FRANJA_ORDER = ['manana', 'mediodia', 'tarde'];

const canaryParts = (d) => {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Atlantic/Canary', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(d);
  const get = (k) => parts.find((p) => p.type === k)?.value;
  return { hour: parseInt(get('hour'), 10) + parseInt(get('minute'), 10) / 60 };
};

// A franja is shown until its end hour has passed (Canary time); the one in progress stays visible
export const remainingFranjas = (franjas, now = new Date()) => {
  if (!franjas?.length) return [];
  const { hour } = canaryParts(now);
  return FRANJA_ORDER.map((k) => franjas.find((f) => f.franja === k)).filter((f) => f && FRANJA_END[f.franja] > hour);
};


const Pill = ({ Icon, color, children, dim }) => (
  <span className="inline-flex items-center gap-1.5" style={{ opacity: dim ? 0.6 : 1 }}>
    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} aria-hidden="true" />
    {Icon && <Icon size={15} strokeWidth={2.2} className="text-gray-700" aria-hidden="true" />}
    {children}
  </span>
);

export const NowBlock = ({ condition, language = 'es', now = new Date(), label = null }) => {
  const view = conditionView(condition, now);
  if (!view.state) return null;
  const t = T[language] || T.es;
  const L = COND_LABELS[language] || COND_LABELS.es;
  const Icon = COND_ICONS[view.icon];
  const short = condition.texto_corto?.[language] || condition.texto_corto?.es || L.icons[view.icon];
  const franjas = remainingFranjas(condition.franjas, now);
  const stateTxt = L.states[view.state];

  return (
    <div data-testid="forecast-now-block" style={{ opacity: view.dimmed ? 0.75 : 1, transition: 'opacity 400ms, background-color 400ms' }}>
      <div className="flex items-center gap-4 px-5 py-4 text-white" style={{ backgroundColor: view.color }} role="status"
        aria-label={`${stateTxt}. ${view.temp} ${L.degrees}, ${short}. ${L.updated} ${view.time}`} data-testid="forecast-now-band" data-state={view.state}>
        {Icon && <Icon size={52} strokeWidth={1.6} className="shrink-0" aria-hidden="true" />}
        <div className="min-w-0 flex-1">
          {label && <p className="text-xs font-semibold opacity-90 mb-1" data-testid="forecast-now-label">{label}</p>}
          <div className="flex items-end gap-3">
            <span className="text-5xl font-extrabold tracking-tight leading-none" data-testid="forecast-now-temp">{view.temp}°</span>
            <span className="text-sm font-semibold pb-1 capitalize" data-testid="forecast-now-state">{stateTxt}</span>
          </div>
          <p className="text-sm mt-1 truncate" data-testid="forecast-now-text">{short}</p>
          <p className="text-[11px] opacity-85 mt-0.5" data-testid="forecast-now-updated">{L.updated} {view.time}</p>
        </div>
      </div>

      {franjas.length > 0 && (
        <div className="px-5 py-3 border-b border-gray-100" data-testid="forecast-franjas">
          <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">{t.rest}</p>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {franjas.map((f) => {
              const FI = COND_ICONS[f.icono];
              const txt = f.texto_corto?.[language] || f.texto_corto?.es;
              return (
                <div key={f.franja} className="flex-1 min-w-[94px] bg-[#FDFBF7] px-2.5 py-2" data-testid={`forecast-franja-${f.franja}`}>
                  <p className="text-xs font-semibold text-[#1D1D1F] leading-tight">{t.franjas[f.franja]}<br /><span className="text-gray-400 font-normal text-[11px]">{t.hours[f.franja]}</span></p>
                  <p className="text-sm mt-1 text-gray-800">
                    <Pill Icon={FI} color={STATE_COLORS[f.estado]}>{f.temp_c != null && <span className="font-bold">{roundHalfUp(f.temp_c)}°</span>}</Pill>
                  </p>
                  {txt && <p className="text-[11px] text-gray-600 mt-1 leading-snug line-clamp-2" data-testid={`forecast-franja-text-${f.franja}`}>{txt}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};

export default NowBlock;
